// useFeedFreshness — derives the hydra feed's CONNECTION health from
// marketStore. Ticks every 1s so the indicator updates even when no new
// messages arrive to trigger a marketStore version bump.
//
// Contract (reworked Jul 20 after the Marty demo): hydra's WS sends market /
// orderbook frames only when something actually CHANGED — coalesced, with no
// minimum cadence — so "no data frame recently" is NORMAL on a thin or pregame
// slate, not a fault. The old 1s staleness bound flapped yellow all demo while
// the backend was fine (the relay's own bounds are 30–600s). Health is now
// judged on the transport:
//
//   disconnected — the WS is down (marketStore.connected false).
//   stalled      — the WS claims connected but NOTHING (data frame, pong,
//                  consensus, anything) has arrived in TRANSPORT_STALL_MS.
//                  Pings run every 30s, so 65s = two missed pongs + slack.
//                  This is the honest "transport is dead" signal.
//   quiet        — connected, keepalives flowing, but no market/orderbook
//                  change in STREAMING_WINDOW_MS. A calm state, not a warning.
//   streaming    — market/orderbook frames within STREAMING_WINDOW_MS.
//
// The old "activity collapse" heuristic (<10% of tickers active in 30s) is
// gone from the DOT: its denominator counted settled/pregame tickers retained
// for up to 30 min, pinning the indicator yellow on every thin slate. The
// resnapshot machinery in useHydra/marketStore keeps its own copy of that
// heuristic for click-refresh/watchdog purposes — recovery, not alarm display.

import { useMarketStore } from '@/stores/marketStore';
import { useNow } from '@/lib/infra/sharedClock';

export type FeedStatus = 'disconnected' | 'stalled' | 'quiet' | 'streaming';

export interface FeedFreshness {
  /** Four-state transport/data health — see module comment. */
  status: FeedStatus;
  /** True only when the feed is genuinely degraded (disconnected or
   *  stalled). Quiet is NOT stale. Kept for existing consumers that
   *  render a warning off this flag. */
  isStale: boolean;
  /** Seconds since the most recent market/orderbook frame. Null until the
   *  first one arrives. */
  secondsSinceUpdate: number | null;
  /** Mirror of marketStore.connected. */
  connected: boolean;
  /** True while hydra has this connection throttled at backpressure
   *  level >= 2 — orderbook frames are suspended server-side (market +
   *  consensus keep flowing). Orderbook-driven views must surface this:
   *  the books on screen are frozen. Clears when orderbook frames resume. */
  orderbooksPaused: boolean;
}

/** Market/orderbook frame within this window → 'streaming'. */
export const STREAMING_WINDOW_MS = 10_000;
/** No frame of ANY kind (incl. pong) for this long while "connected" →
 *  'stalled'. Ping interval is 30s; 65s = two missed pongs + slack. */
export const TRANSPORT_STALL_MS = 65_000;

export interface FeedStatusInputs {
  connected: boolean;
  /** Last market/orderbook ingest (marketStore.lastMessageMs); 0 = never. */
  lastDataMs: number;
  /** Last WS frame of any kind incl. pong (marketStore keepalive); 0 = never. */
  lastKeepaliveMs: number;
  now: number;
}

/** Pure state derivation — exported for tests. */
export function computeFeedStatus(i: FeedStatusInputs): FeedStatus {
  if (!i.connected) return 'disconnected';
  const lastAny = Math.max(i.lastDataMs, i.lastKeepaliveMs);
  if (lastAny > 0 && i.now - lastAny > TRANSPORT_STALL_MS) return 'stalled';
  if (i.lastDataMs > 0 && i.now - i.lastDataMs <= STREAMING_WINDOW_MS) return 'streaming';
  return 'quiet';
}

export function useFeedFreshness(): FeedFreshness {
  const connected = useMarketStore((s) => s.connected);
  const lastMessageMs = useMarketStore((s) => s.lastMessageMs);
  const orderbooksPaused = useMarketStore((s) => s.backpressureLevel >= 2);
  const tickNow = useNow();

  // Keepalive time is kept out of React state on purpose (it can bump on
  // every WS frame); read it imperatively on the 1s tick like the other
  // store probes.
  const lastKeepaliveMs = useMarketStore.getState().getLastKeepaliveMs();
  const status = computeFeedStatus({
    connected,
    lastDataMs: lastMessageMs,
    lastKeepaliveMs,
    now: tickNow,
  });

  const ageMs = lastMessageMs > 0 ? tickNow - lastMessageMs : null;
  const secondsSinceUpdate = ageMs == null ? null : Math.max(0, Math.floor(ageMs / 1000));

  return {
    status,
    isStale: status === 'disconnected' || status === 'stalled',
    secondsSinceUpdate,
    connected,
    orderbooksPaused,
  };
}
